/**
 * WEB-04 compare rules (pure): at most 4 units, values normalised to one unit
 * per row, and a missing value is reported explicitly (never blank, never invented).
 * No benchmark scores (PRD C-03).
 */
import { batteryShown, conditionBadge, gradeApplies } from './listing';
import { formatValue, renderTemplate } from '@/lib/productTypes';
import { attrsOf } from './shop';

export const COMPARE_LIMIT = 4;
export const MISSING = null;

/**
 * → { ids, error } — error is set (and ids unchanged) when adding past the limit,
 * or (B11-P3) when the unit is of another product type than those already picked:
 * `typeOf(id)` returns a listing's type key when known.
 */
export function toggleCompare(ids, id, limit = COMPARE_LIMIT, typeOf = null) {
  const list = (ids || []).map(Number);
  const n = Number(id);
  if (list.includes(n)) return { ids: list.filter((x) => x !== n), error: null };
  if (list.length >= limit) return { ids: list, error: `You can compare up to ${limit} items. Remove one to add another.` };
  if (typeOf && list.length && typeOf(list[0]) && typeOf(n) && typeOf(list[0]) !== typeOf(n)) {
    return { ids: list, error: 'Compare works within one kind of product. Clear the compare list to start a new one.' };
  }
  return { ids: [...list, n], error: null };
}

export function parseCompareIds(value, limit = COMPARE_LIMIT) {
  const ids = String(value || '')
    .split(',')
    .map((s) => parseInt(s, 10))
    .filter((n) => Number.isInteger(n) && n > 0);
  return [...new Set(ids)].slice(0, limit);
}

const v = (x) => (x === undefined || x === null || x === '' ? MISSING : x);

/**
 * Rows for the compare matrix. `extras` maps listing id → { inspection, delivery, terms }
 * (fixture-backed today). Each row: { key, label, values: [string|null per unit], best? }.
 */
export function compareRows(listings, extras = {}, type = null) {
  const ex = (l) => extras[l.id] || {};
  const rows = [
    { key: 'price', label: 'Price (incl. GST)', get: (l) => v(l.price_paise), money: true },
    { key: 'mrp', label: 'MRP', get: (l) => v(l.mrp_paise), money: true },
    { key: 'allin', label: 'All-in price (with delivery)', get: (l) => (ex(l).delivery && ex(l).delivery.shipping_paise != null && l.price_paise != null ? l.price_paise + ex(l).delivery.shipping_paise : MISSING), money: true },
    { key: 'stock', label: 'Stock', get: (l) => (l.published_at ? 'In stock · 1 unit (this exact item)' : MISSING) },
    { key: 'delivery', label: 'Delivery', get: (l) => v(ex(l).delivery && ex(l).delivery.label) },
    { key: 'grade', label: 'Condition grade', get: (l) => (gradeApplies(l) && l.grade ? `Grade ${l.grade}` : MISSING) },
    { key: 'condition', label: 'Condition', get: (l) => v(conditionBadge(l)) },
    { key: 'inspection', label: 'Inspection result', get: (l) => v(ex(l).inspection && `${ex(l).inspection.passed}/${ex(l).inspection.total} checks passed`) },
    { key: 'battery', label: 'Battery health', get: (l) => (batteryShown(l) ? `${l.battery_health_pct}%` : MISSING) },
    ...specCompareRows(type),
    { key: 'warranty', label: 'Seller warranty', get: (l) => (l.warranty_months ? `${l.warranty_months} months` : MISSING) },
    { key: 'returns', label: 'Return window', get: (l) => v(ex(l).terms && ex(l).terms.return_days && `${ex(l).terms.return_days} days`) },
    { key: 'serial', label: 'Serial (masked)', get: (l) => v(l.serial_masked) },
  ];
  return rows.map((r) => ({ key: r.key, label: r.label, money: !!r.money, values: listings.map((l) => r.get(l)) }));
}

/** Spec rows: the type's `views.compare` templates when it declares them, else its `compare` attributes (B11-P3). */
function specCompareRows(type) {
  if (!type) return [];
  const view = type.views && type.views.compare;
  if (view) return view.map((r) => ({ key: r.key, label: r.label, get: (l) => v(renderTemplate(r.text, attrsOf(l))) }));
  return type.attributes.filter((a) => a.compare).map((a) => ({ key: `attr_${a.key}`, label: a.label, get: (l) => v(formatValue(a, attrsOf(l)[a.key])) }));
}

/** Index of the lowest non-missing numeric value in a money row (for a "lowest" hint), or -1. */
export function lowestIndex(values) {
  let best = -1;
  values.forEach((x, i) => {
    if (typeof x === 'number' && (best === -1 || x < values[best])) best = i;
  });
  return values.filter((x) => typeof x === 'number').length > 1 ? best : -1;
}
